import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { usePageTitle } from "../../hooks/usePageTitle";

interface OrderSummary {
  id: number;
  order_number: string;
  created_at: string;
  total: number;
  status: string;
  items_count: number;
}

interface MessageSummary {
  id: number;
  subject: string;
  created_at: string;
  status: "new" | "read" | "replied";
  order_number: string | null;
}

interface CustomerStats {
  totalOrders: number;
  totalSpent: number;
  activeSupportTickets: number;
}

export default function Dashboard() {
  usePageTitle("My Account");
  const { customer, logout } = useCustomerAuth();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [stats, setStats] = useState<CustomerStats>({ totalOrders: 0, totalSpent: 0, activeSupportTickets: 0 });
  const [loading, setLoading] = useState(true);
  const [ordersPage, setOrdersPage] = useState(1);
  const [messagesPage, setMessagesPage] = useState(1);

  useEffect(() => {
    fetchDashboardData();
  }, [ordersPage, messagesPage]);

  const fetchDashboardData = async () => {
    try {
      // Fetch orders
      const ordersResponse = await fetch(`/api/customer/orders?page=${ordersPage}&limit=5`, {
        credentials: "include",
      });
      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        setOrders(ordersData.orders || []);
      }

      // Fetch messages
      const messagesResponse = await fetch(`/api/customer/messages?page=${messagesPage}&limit=5`, {
        credentials: "include",
      });
      if (messagesResponse.ok) {
        const messagesData = await messagesResponse.json();
        setMessages(messagesData.messages || []);
      }

      // Fetch stats
      const statsResponse = await fetch("/api/customer/stats", {
        credentials: "include",
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getMessageStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">New</span>;
      case "read":
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">Read</span>;
      case "replied":
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Replied</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Profile Summary Card */}
      <div className="card mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {customer?.avatarUrl ? (
              <img
                src={customer.avatarUrl}
                alt={`${customer.firstName} ${customer.lastName}`}
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-600">
                  {customer?.firstName.charAt(0)}{customer?.lastName.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">
                Welcome back, {customer?.firstName}!
              </h1>
              <p className="text-gray-600">{customer?.email}</p>
              {customer?.oauthProvider && (
                <p className="text-sm text-gray-500">
                  Signed in with {customer.oauthProvider === "google" ? "Google" : "Facebook"}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/account/profile" className="btn-secondary">
              Edit Profile
            </Link>
            <button onClick={handleLogout} className="btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-600 mb-1">Total Orders</h3>
          <p className="text-3xl font-bold">{stats.totalOrders}</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-600 mb-1">Total Spent</h3>
          <p className="text-3xl font-bold">{formatCurrency(stats.totalSpent)}</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-600 mb-1">Active Support Tickets</h3>
          <p className="text-3xl font-bold">{stats.activeSupportTickets}</p>
        </div>
      </div>

      {/* Order History Section */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Recent Orders</h2>
          {orders.length > 0 && (
            <Link to="/account/orders" className="text-sm text-black underline hover:no-underline">
              View All Orders
            </Link>
          )}
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">You haven't placed any orders yet.</p>
            <Link to="/products" className="btn-primary">
              Shop Now
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2">Order Number</th>
                  <th className="text-left py-3 px-2">Date</th>
                  <th className="text-left py-3 px-2">Items</th>
                  <th className="text-left py-3 px-2">Total</th>
                  <th className="text-left py-3 px-2">Status</th>
                  <th className="text-left py-3 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium">{order.order_number}</td>
                    <td className="py-3 px-2 text-gray-600">{formatDate(order.created_at)}</td>
                    <td className="py-3 px-2 text-gray-600">{order.items_count} items</td>
                    <td className="py-3 px-2 font-medium">{formatCurrency(order.total)}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 text-xs rounded ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <Link
                        to={`/account/orders/${order.order_number}`}
                        className="text-sm text-black underline hover:no-underline"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Support Tickets Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Support Tickets</h2>
          <Link to="/contact" className="btn-secondary">
            Contact Us
          </Link>
        </div>

        {messages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">You haven't submitted any inquiries yet.</p>
            <Link to="/contact" className="btn-primary">
              Contact Us
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => (
              <Link
                key={message.id}
                to={`/account/messages/${message.id}`}
                className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-medium">{message.subject}</h3>
                    {message.order_number && (
                      <p className="text-sm text-gray-600">Order: {message.order_number}</p>
                    )}
                  </div>
                  {getMessageStatusBadge(message.status)}
                </div>
                <p className="text-sm text-gray-500">{formatDate(message.created_at)}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
